<template>
  <!-- `display: contents` : les trois cards remontent dans la grille du dashboard parent
       (`.cards-grid`), exactement comme quand la saisie était inline dans SupervisorDashboard.
       Les modales sont en `position: fixed`, indépendantes de la grille. -->
  <div class="supervisor-feedback-saisie">
    <!-- Card 1 — Appréciation globale -->
    <dashboard-card
      title="😊 Appréciation globale"
      description="Donner votre évaluation du repas"
      :badge="getRatingLabel(globalRating)"
      badge-variant="green"
      link-label="Évaluer"
      variant="featured"
      @click="showGlobalFeedback = true"
    />

    <!-- Card 2 — Détail par plat -->
    <dashboard-card
      title="🍽️ Détail par plat"
      description="Noter chaque plat individuellement"
      :badge="`${dishes.length} plats`"
      link-label="Noter"
      @click="showDetailedFeedback = true"
    />

    <!-- Card 3 — Contexte du jour -->
    <dashboard-card
      title="🌤️ Contexte du jour"
      description="Météo, ambiance et observations"
      link-label="Renseigner"
      @click="showContextForm = true"
    />

    <!-- Modal: Appréciation globale -->
    <div v-if="showGlobalFeedback" class="modal-overlay" @click="showGlobalFeedback = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" aria-label="Fermer" @click="showGlobalFeedback = false">✕</button>
        <h2 class="modal-title">😊 Appréciation globale</h2>
        <p class="modal-subtitle">Comment s'est passé le repas ?</p>
        <div class="rating-selector">
          <button
            v-for="(emoji, idx) in emojis"
            :key="idx"
            @click="globalRating = idx + 1"
            :class="{ active: globalRating === idx + 1 }"
            class="rating-btn"
          >
            <span class="rating-emoji">{{ emoji }}</span>
            <span class="rating-label">{{ getRatingLabelByIndex(idx) }}</span>
          </button>
        </div>
        <button v-if="globalRating" @click="saveGlobalRating" class="btn btn-primary">Enregistrer</button>
      </div>
    </div>

    <!-- Modal: Détail par plat -->
    <div v-if="showDetailedFeedback" class="modal-overlay" @click="showDetailedFeedback = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" aria-label="Fermer" @click="showDetailedFeedback = false">✕</button>
        <h2 class="modal-title">🍽️ DÉTAIL PAR PLAT</h2>
        <div class="dishes-list">
          <div v-for="(dish, idx) in dishes" :key="idx" class="dish-card">
            <div class="flex justify-between items-start mb-3">
              <div>
                <h4 class="dish-name">{{ dish.name }}</h4>
                <p class="text-xs text-gray-500">{{ dish.category }}</p>
              </div>
            </div>
            <div class="mb-3">
              <p class="text-xs text-gray-600 mb-1">Appréciation</p>
              <div class="dish-ratings">
                <button
                  v-for="(emoji, i) in emojis"
                  :key="i"
                  @click="updateDishRating(idx, i + 1)"
                  :class="['rating-mini-btn', { active: dish.rating === i + 1 }]"
                >
                  {{ emoji }}
                </button>
                <span class="rating-percent">{{ dish.rating ? getRatingLabel(dish.rating) : "" }}</span>
              </div>
            </div>
            <div v-if="dish.comment" class="dish-comment">💬 "{{ dish.comment }}"</div>
            <button @click="editDishComment(idx)" class="text-xs text-blue-600 mt-2 hover:underline">
              {{ dish.comment ? "Modifier" : "Ajouter" }} un commentaire
            </button>
          </div>
        </div>
        <button @click="saveDishRatings" class="btn btn-primary">Terminé</button>
      </div>
    </div>

    <!-- Modal: Contexte -->
    <div v-if="showContextForm" class="modal-overlay" @click="showContextForm = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" aria-label="Fermer" @click="showContextForm = false">✕</button>
        <h2 class="modal-title">🌤️ Contexte du jour</h2>
        <div class="detail-section">
          <h3 class="section-title">Météo</h3>
          <div class="weather-selector">
            <button
              v-for="w in weatherOptions"
              :key="w.key"
              @click="weather = w.key"
              :class="{ active: weather === w.key }"
              class="weather-btn"
            >
              <span class="weather-icon">{{ w.icon }}</span>
              <span class="weather-label">{{ w.label }}</span>
            </button>
          </div>
        </div>
        <div class="detail-section">
          <h3 class="section-title">Température</h3>
          <div class="temperature-input">
            <input
              v-model.number="temperature"
              type="number"
              class="input-field"
              aria-label="Température en °C"
              placeholder="22"
            />
            <span class="temperature-unit">°C</span>
          </div>
        </div>
        <div class="detail-section">
          <h3 class="section-title">Niveau sonore</h3>
          <div class="noise-selector">
            <button
              v-for="n in noiseOptions"
              :key="n.key"
              @click="noiseLevel = n.key"
              :class="{ active: noiseLevel === n.key }"
              class="noise-btn"
            >
              <span class="noise-icon">{{ n.icon }}</span>
              <span class="noise-label">{{ n.label }}</span>
            </button>
          </div>
        </div>
        <button @click="saveContext" class="btn btn-primary">Enregistrer</button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Saisie du retour détaillé du personnel (SupervisorFeedback) — capacité supervisor-feedback.
 *
 * Extrait de SupervisorDashboard pour être partagé par les dashboards MANAGER, SUPERVISOR et
 * WORKER : depuis l'arbitrage §3 de `meal-feedback-restitution`, le « DÉTAIL PAR PLAT » est
 * saisi par tout membre du personnel (auteur `author`), et non plus par le seul superviseur.
 * L'aiguillage entre ce panneau et « Mon avis sur le repas du jour » (MealFeedbackPanel) est
 * porté par le flag de cantine `show_detailed_feedback`, décidé dans le dashboard parent.
 *
 * Autonome : cantine, service et date arrivent en props (contexte du dashboard), le panneau
 * charge lui-même le menu du jour et enregistre via l'endpoint `supervisorFeedback/`.
 */
import DashboardCard from "@/components/DashboardCard.vue"

export default {
  name: "SupervisorFeedbackPanel",
  components: { DashboardCard },

  props: {
    canteenId: { type: [Number, String], default: null },
    serviceId: { type: [Number, String], default: null },
    date: { type: String, default: null },
  },

  data() {
    return {
      apiBaseUrl: "/api/v1",

      // Modales
      showGlobalFeedback: false,
      showDetailedFeedback: false,
      showContextForm: false,

      // Saisie
      emojis: ["😞", "😕", "😐", "😊", "😍"],
      globalRating: null,
      noiseLevel: 3,
      noiseOptions: [
        { key: 1, icon: "😊", label: "Calme" },
        { key: 2, icon: "🙂", label: "Plutôt calme" },
        { key: 3, icon: "😐", label: "Normal" },
        { key: 4, icon: "😟", label: "Bruyant" },
        { key: 5, icon: "😱", label: "Très bruyant" },
      ],
      temperature: 22,
      weather: "sunny",
      weatherOptions: [
        { key: "sunny", icon: "☀️", label: "Ensoleillé" },
        { key: "cloudy", icon: "☁️", label: "Nuageux" },
        { key: "rainy", icon: "🌧️", label: "Pluvieux" },
      ],
      dishes: [],

      supervisorFeedbackId: null,
      isSavingFeedback: false,
    }
  },

  watch: {
    canteenId() {
      this.loadMenuItems()
    },
    date() {
      this.loadMenuItems()
    },
  },

  mounted() {
    this.loadMenuItems()
  },

  methods: {
    async loadMenuItems() {
      // Réinitialiser avant chaque chargement : un changement de cantine/date repart à zéro.
      this.dishes = []
      this.supervisorFeedbackId = null
      if (!this.canteenId || !this.date) return
      const catOrder = ["entree", "plat", "accompagnement", "produit_laitier", "dessert", "sans_viande"]
      const catLabels = {
        entree: "Entrée",
        plat: "Plat principal",
        accompagnement: "Accompagnement",
        produit_laitier: "Produit laitier",
        dessert: "Dessert",
        sans_viande: "Sans viande",
      }
      try {
        const resp = await fetch(`/api/v1/canteen/${this.canteenId}/menuItems/?date=${this.date}`, {
          credentials: "same-origin",
        })
        const menuItems = resp.ok ? await resp.json() : []
        if (menuItems.length > 0) {
          this.dishes = catOrder
            .filter((cat) => menuItems.some((i) => i.category === cat))
            .map((cat) => {
              const item = menuItems.find((i) => i.category === cat)
              return { apiCategory: cat, name: item.name, category: catLabels[cat] || cat, rating: null, comment: "" }
            })
        } else {
          // Aucun menu importé : catégories génériques pour permettre la notation.
          this.dishes = ["entree", "plat", "accompagnement", "dessert"].map((cat) => ({
            apiCategory: cat,
            name: catLabels[cat],
            category: catLabels[cat],
            rating: null,
            comment: "",
          }))
        }
      } catch {
        this.dishes = []
      }
    },

    getRatingLabel(rating) {
      const labels = ["Très mauvais", "Mauvais", "Correct", "Très bien", "Excellent"]
      return rating ? labels[rating - 1] : "Non évalué"
    },
    getRatingLabelByIndex(idx) {
      const labels = ["Très mauvais", "Mauvais", "Correct", "Très bien", "Excellent"]
      return labels[idx]
    },
    updateDishRating(idx, rating) {
      this.dishes[idx].rating = rating
    },
    editDishComment(idx) {
      const c = prompt("Commentaire pour " + this.dishes[idx].name + ":", this.dishes[idx].comment || "")
      if (c !== null) this.dishes[idx].comment = c
    },

    async saveSupervisorFeedback(fields) {
      if (!this.canteenId || !this.serviceId) return null
      const csrfToken = document.cookie.match(/csrftoken=([^;]+)/)?.[1] || ""
      const headers = { "X-CSRFToken": csrfToken }
      let url = `${this.apiBaseUrl}/canteen/${this.canteenId}/supervisorFeedback/`
      let method = "POST"
      const body = {
        ...fields,
        meal_date: this.date,
        service: this.serviceId,
      }
      if (this.supervisorFeedbackId) {
        url = `${this.apiBaseUrl}/canteen/${this.canteenId}/supervisorFeedback/${this.supervisorFeedbackId}/`
        method = "PATCH"
      }
      try {
        this.isSavingFeedback = true
        const resp = await fetch(url, {
          method,
          credentials: "same-origin",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify(body),
        })
        if (resp.ok) {
          const data = await resp.json()
          this.supervisorFeedbackId = data.id
          this.$emit("saved", data)
          return data
        }
        // 409 = doublon unique_together → récupérer l'existant puis réessayer.
        if (resp.status === 409) {
          const existing = await fetch(
            `${this.apiBaseUrl}/canteen/${this.canteenId}/supervisorFeedback/?date=${this.date}&service_id=${this.serviceId}`,
            { credentials: "same-origin" }
          )
          if (existing.ok) {
            const list = await existing.json()
            if (list.length > 0) {
              this.supervisorFeedbackId = list[0].id
              return await this.saveSupervisorFeedback(fields)
            }
          }
        }
      } catch (err) {
        console.error("Erreur sauvegarde retour détaillé:", err)
      } finally {
        this.isSavingFeedback = false
      }
      return null
    },

    async saveGlobalRating() {
      await this.saveSupervisorFeedback({ global_rating: this.globalRating })
      this.showGlobalFeedback = false
    },

    async saveContext() {
      await this.saveSupervisorFeedback({
        context_notes: {
          noise: this.noiseLevel,
          temperature: this.temperature,
          weather: this.weather,
        },
      })
      this.showContextForm = false
    },

    async saveDishRatings() {
      const dishRatings = {}
      this.dishes.forEach((dish) => {
        if (dish.rating !== null) {
          dishRatings[dish.apiCategory] = dish.rating
        }
      })
      await this.saveSupervisorFeedback({ dish_ratings: dishRatings })
      this.showDetailedFeedback = false
    },
  },
}
</script>

<style scoped lang="scss">
// Les cards remontent dans la grille du dashboard parent.
.supervisor-feedback-saisie {
  display: contents;
}

/* Modales — mêmes classes que celles héritées de SupervisorDashboard, recopiées ici
   parce que les styles sont `scoped` et ne traversent pas la frontière de composant. */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
}
.modal-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}
.modal-large {
  max-width: 700px;
}
.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  color: #6b7280;
}
.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}
.modal-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1.5rem 0;
}

/* Rating selector */
.rating-selector {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}
.rating-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 0.5rem;
  background: #f9fafb;
  border: 2px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}
.rating-btn:hover {
  background: #eff6ff;
}
.rating-btn.active {
  background: #eff6ff;
  border-color: #2563eb;
  transform: scale(1.05);
}
.rating-emoji {
  font-size: 2.5rem;
}
.rating-label {
  font-size: 0.7rem;
  font-weight: 500;
  color: #6b7280;
  text-align: center;
}

/* Dishes */
.dishes-list {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-bottom: 1.5rem;
}
.dish-card {
  background: #f9fafb;
  padding: 1.25rem;
  border-radius: 12px;
}
.dish-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}
.dish-ratings {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.rating-mini-btn {
  font-size: 1.5rem;
  background: white;
  border: 2px solid transparent;
  border-radius: 8px;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
}
.rating-mini-btn.active {
  border-color: #2563eb;
  background: #eff6ff;
  transform: scale(1.1);
}
.rating-percent {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
}
.dish-comment {
  font-size: 0.875rem;
  color: #f97316;
  font-style: italic;
  margin: 0.75rem 0 0 0;
}

/* Context */
.detail-section {
  margin-bottom: 1.5rem;
}
.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}
.weather-selector {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
}
.weather-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: #f9fafb;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}
.weather-btn.active {
  background: #eff6ff;
  border-color: #2563eb;
}
.weather-icon {
  font-size: 2.5rem;
}
.weather-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
}
.temperature-input {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.input-field {
  flex: 1;
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
}
.input-field:focus {
  outline: none;
  border-color: #2563eb;
}
.temperature-unit {
  font-size: 1.125rem;
  font-weight: 600;
  color: #6b7280;
}
.noise-selector {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}
.noise-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s;
}
.noise-btn.active {
  border-color: #3b82f6;
  background: #eff6ff;
}
.noise-icon {
  font-size: 1.5rem;
}
.noise-label {
  font-size: 0.75rem;
  color: #6b7280;
}

/* Buttons */
.btn {
  display: block;
  width: 100%;
  padding: 0.875rem;
  border-radius: 8px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  margin-top: 0.75rem;
  font-size: 1rem;
}
.btn-primary {
  background: #2563eb;
  color: white;
}
</style>
